import { mapNotificationTemplatesFromApi } from '@simOn/common/notifications';
import { TaskTicketDetail, TaskType, TaskTypeValue } from '@simOn/ticket/element/models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { TaskFormDraft } from 'feature-task';
import { AgentTicketPayload } from '../model';

/**
 * `TaskPlacementDraft.checklistSourceId` value for "no checklist": `ConnectChecklistComponent` ignores an empty
 * initial id (instead of falling back to the ticket's checklist), so nothing is selected and the PUT sends
 * `checklist: null`.
 */
export const NO_CHECKLIST = '';

export interface TicketDraftSource {
  /** Card payload: complete for a create card, only the changed values (`null` = unchanged) for an update card. */
  payload: Partial<AgentTicketPayload>;
  /** Payload properties the card removes (update cards). */
  clearedFields?: readonly string[];
  /** The ticket as it is now (update cards); its values fill whatever the payload leaves out. */
  current?: TaskTicketDetail | null;
}

/** Tickets may carry the task type as an enum name or a number (like `AddTaskComponent._resolveTaskType`). */
const resolveTaskType = (taskType: TaskTicketDetail['taskType'] | null): TaskTypeValue | undefined => {
  if (typeof taskType === 'number') return taskType as TaskTypeValue;
  if (typeof taskType === 'string' && taskType in TaskType) return TaskType[taskType as keyof typeof TaskType];
  return undefined;
};

/**
 * Form values of the task form (`TaskFormDraft`) for a card: the payload wins, a cleared field becomes empty,
 * everything else keeps the current ticket's value. Times are passed verbatim, as the form shows them.
 */
export const toTicketFormDraft = ({ payload, clearedFields = [], current }: TicketDraftSource): TaskFormDraft => {
  const cleared = (field: keyof AgentTicketPayload): boolean => clearedFields.includes(field);
  const pick = (field: 'roomId' | 'assetId' | 'contractorId'): string | undefined =>
    cleared(field) ? undefined : (payload[field] ?? current?.[field] ?? undefined);
  const dueCleared = cleared('dueDate');

  return {
    name: payload.name ?? current?.name ?? '',
    taskType: resolveTaskType(payload.taskType ?? current?.taskType ?? null),
    assigneeIds: cleared('assigneeIds')
      ? []
      : (payload.assigneeIds ?? current?.assignments?.map(({ userId }) => userId) ?? []),
    roomId: pick('roomId'),
    description: cleared('htmlText') ? undefined : (payload.htmlText ?? current?.htmlText ?? undefined),
    dueDate: dueCleared ? undefined : (payload.dueDate ?? current?.dueDate ?? undefined),
    dueTime: dueCleared ? undefined : (payload.dueTime ?? current?.dueTime)?.slice(0, 5),
    assetId: pick('assetId'),
    contractorId: pick('contractorId'),
    notificationTemplates: cleared('notificationTemplates')
      ? []
      : mapNotificationTemplatesFromApi(payload.notificationTemplates ?? current?.notificationTemplates)
  };
};

/** Checklist template the form should connect: {@link NO_CHECKLIST} when removed, else the new or current id. */
export const toTicketChecklistSourceId = ({ payload, clearedFields = [], current }: TicketDraftSource): string | null => {
  if (clearedFields.includes('checklist')) return NO_CHECKLIST;
  return payload.checklist?.sourceChecklistId ?? current?.checklist?.sourceChecklistId ?? null;
};
